import { useState, useCallback } from 'react';
import { Play, Copy, Check, ExternalLink, Code, Loader2 } from 'lucide-react';
import styles from './DemoPlay.module.css';

interface GameDemo {
	id: string;
	name: string;
	description: string;
	available: boolean;
}

const demoGames: GameDemo[] = [
	{ id: 'dice', name: 'Dice', description: 'Classic provably fair dice game with adjustable win chance', available: true },
	{ id: 'plinko', name: 'Plinko', description: 'Drop the ball and watch it bounce through pegs', available: true },
	{ id: 'crash', name: 'Crash', description: 'Cash out before the multiplier crashes', available: true },
	{ id: 'coinflip', name: 'Coin Flip', description: 'Simple 50/50 coin flip game', available: true },
];

// Get the game client base URL based on environment
function getGameClientUrl(): string {
	if (typeof window === 'undefined') return 'https://play.rngfort.com';

	const hostname = window.location.hostname;

	// Local development - operator dashboard is on 5175, game client is on 5173
	if (hostname === 'localhost' || hostname === '127.0.0.1') {
		return 'http://localhost:5173';
	}

	// Production - replace operator. with play.
	return window.location.origin.replace('operator.', 'play.');
}

export default function DemoPlay() {
	const [selectedGame, setSelectedGame] = useState<string | null>(null);
	const [showUrlModal, setShowUrlModal] = useState(false);
	const [copiedUrl, setCopiedUrl] = useState(false);
	const [copiedEmbed, setCopiedEmbed] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [demoUrl, setDemoUrl] = useState<string | null>(null);
	const [isLoadingDemo, setIsLoadingDemo] = useState(false);
	const [demoError, setDemoError] = useState<string | null>(null);

	const gameClientUrl = getGameClientUrl();

	// Create a demo session via API and get the game URL
	const createDemoSession = useCallback(async (gameId: string): Promise<string | null> => {
		setIsLoadingDemo(true);
		setDemoError(null);

		try {
			const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
			const response = await fetch(`${API_URL}/api/v1/operators/sessions`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					// Use sandbox API key for demo sessions
					'Authorization': 'Bearer sk_test_demosecret001',
				},
				body: JSON.stringify({
					playerId: `demo_${Date.now()}`,
					currency: 'USD',
					walletMode: 'integrated',
					demo: true,
				}),
			});

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(data.error?.message || 'Failed to create demo session');
			}

			// Construct URL with token for game client
			const token = data.data.sessionToken;
			const url = `${gameClientUrl}?token=${token}&game=${gameId}`;
			return url;
		} catch (error) {
			console.error('Failed to create demo session:', error);
			setDemoError(error instanceof Error ? error.message : 'Failed to create demo session');
			return null;
		} finally {
			setIsLoadingDemo(false);
		}
	}, [gameClientUrl]);

	const getEmbedCode = (url: string) => {
		return `<iframe
  src="${url}"
  width="100%"
  height="600"
  frameborder="0"
  allow="fullscreen"
  style="border-radius: 8px; max-width: 800px;"
></iframe>`;
	};

	const handleCopyUrl = async () => {
		if (!demoUrl) return;
		await navigator.clipboard.writeText(demoUrl);
		setCopiedUrl(true);
		setTimeout(() => setCopiedUrl(false), 2000);
	};

	const handleCopyEmbed = async () => {
		if (!demoUrl) return;
		await navigator.clipboard.writeText(getEmbedCode(demoUrl));
		setCopiedEmbed(true);
		setTimeout(() => setCopiedEmbed(false), 2000);
	};

	const openUrlModal = async (gameId: string) => {
		setSelectedGame(gameId);
		const url = await createDemoSession(gameId);
		if (url) {
			setDemoUrl(url);
			setShowUrlModal(true);
		}
	};

	const openPreview = async (gameId: string) => {
		setSelectedGame(gameId);
		const url = await createDemoSession(gameId);
		if (url) {
			setDemoUrl(url);
			setShowPreview(true);
		}
	};

	return (
		<div className={styles.demoPlay}>
			<header className={styles.header}>
				<div>
					<h1 className={styles.title}>Demo Play</h1>
					<p className={styles.subtitle}>Try games and generate demo URLs for your website</p>
				</div>
			</header>

			<div className={styles.infoCard}>
				<h3>Demo Mode Features</h3>
				<ul>
					<li>Players receive a virtual balance to try games risk-free</li>
					<li>No real money transactions - perfect for previews</li>
					<li>Demo URLs work without authentication</li>
					<li>Embed games directly on your website for visitor engagement</li>
				</ul>
			</div>

			<div className={styles.gamesGrid}>
				{demoGames.map((game) => (
					<div key={game.id} className={`${styles.gameCard} ${!game.available ? styles.unavailable : ''}`}>
						<div className={styles.gameHeader}>
							<div className={styles.gameIcon}>
								<GameIcon type={game.id} />
							</div>
							{!game.available && <span className={styles.comingSoon}>Coming Soon</span>}
						</div>
						<h3 className={styles.gameName}>{game.name}</h3>
						<p className={styles.gameDescription}>{game.description}</p>

						<div className={styles.gameActions}>
							<button
								className={styles.tryBtn}
								onClick={() => openPreview(game.id)}
								disabled={!game.available}
							>
								<Play size={16} />
								Try Demo
							</button>
							<button
								className={styles.urlBtn}
								onClick={() => openUrlModal(game.id)}
								disabled={!game.available}
							>
								<Code size={16} />
								Get URL
							</button>
						</div>
					</div>
				))}
			</div>

			{/* URL Modal */}
			{showUrlModal && selectedGame && demoUrl && (
				<div className={styles.modalOverlay} onClick={() => { setShowUrlModal(false); setDemoUrl(null); }}>
					<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
						<h2>Demo URL for {demoGames.find((g) => g.id === selectedGame)?.name}</h2>
						<p className={styles.modalDescription}>
							Use these URLs to embed the demo game on your website or share with users.
						</p>

						<div className={styles.urlSection}>
							<label>Direct Link</label>
							<div className={styles.urlBox}>
								<code>{demoUrl}</code>
								<button onClick={handleCopyUrl} className={styles.copyBtn}>
									{copiedUrl ? <Check size={16} /> : <Copy size={16} />}
								</button>
							</div>
							<a
								href={demoUrl}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.openLink}
							>
								<ExternalLink size={14} />
								Open in new tab
							</a>
						</div>

						<div className={styles.urlSection}>
							<label>Embed Code</label>
							<div className={styles.embedBox}>
								<pre>{getEmbedCode(demoUrl)}</pre>
								<button onClick={handleCopyEmbed} className={styles.copyBtn}>
									{copiedEmbed ? <Check size={16} /> : <Copy size={16} />}
								</button>
							</div>
						</div>

						<button className={styles.closeBtn} onClick={() => { setShowUrlModal(false); setDemoUrl(null); }}>
							Close
						</button>
					</div>
				</div>
			)}

			{/* Loading Overlay */}
			{isLoadingDemo && (
				<div className={styles.modalOverlay}>
					<div className={styles.loadingModal}>
						<Loader2 size={32} className={styles.spinner} />
						<p>Creating demo session...</p>
					</div>
				</div>
			)}

			{/* Error Modal */}
			{demoError && (
				<div className={styles.modalOverlay} onClick={() => setDemoError(null)}>
					<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
						<h2>Error</h2>
						<p className={styles.errorText}>{demoError}</p>
						<button className={styles.closeBtn} onClick={() => setDemoError(null)}>
							Close
						</button>
					</div>
				</div>
			)}

			{/* Preview Modal */}
			{showPreview && selectedGame && demoUrl && (
				<div className={styles.modalOverlay} onClick={() => { setShowPreview(false); setDemoUrl(null); }}>
					<div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
						<div className={styles.previewHeader}>
							<h2>{demoGames.find((g) => g.id === selectedGame)?.name} Demo</h2>
							<button className={styles.closePreviewBtn} onClick={() => { setShowPreview(false); setDemoUrl(null); }}>
								&times;
							</button>
						</div>
						<div className={styles.previewContent}>
							<iframe
								src={demoUrl}
								title={`${selectedGame} Demo`}
								className={styles.previewIframe}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

function GameIcon({ type }: { type: string }) {
	switch (type) {
		case 'dice':
			return (
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<rect x="3" y="3" width="18" height="18" rx="2" />
					<circle cx="8" cy="8" r="1.5" fill="currentColor" />
					<circle cx="16" cy="16" r="1.5" fill="currentColor" />
					<circle cx="12" cy="12" r="1.5" fill="currentColor" />
				</svg>
			);
		case 'plinko':
			return (
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<circle cx="12" cy="4" r="2" />
					<circle cx="6" cy="10" r="1.5" />
					<circle cx="12" cy="10" r="1.5" />
					<circle cx="18" cy="10" r="1.5" />
					<path d="M3 22h18" />
				</svg>
			);
		case 'crash':
			return (
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<path d="M3 17l6-6 4 4 8-11" />
					<path d="M17 4h4v4" />
				</svg>
			);
		default:
			return (
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<circle cx="12" cy="12" r="10" />
					<path d="M12 6v2M12 16v2" />
				</svg>
			);
	}
}
